import { Services } from "@/services/services"


export interface fetchWithTokenOptions extends RequestInit {
    token?: string
    lang?: string
}

export async function fetchWithToken(url: string, options: fetchWithTokenOptions = {}): Promise<Response> {
    const headers = new Headers(options.headers)
    if (options?.token) {
        headers.set('Authorization', `Bearer ${options.token}`)
    }
    if (options?.lang) {
        headers.set('Accept-Language', options.lang)
    }
    return fetch(url, {
        ...options,
        headers,
    })
}

export async function fetchJSONWithToken<T>(url: string, options: fetchWithTokenOptions = {}): Promise<T> {
    return fetchWithToken(url, options).then(resp => decodeFetchResponse<T>(resp))
}

export async function decodeFetchResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("Content-Type");
    if (!contentType || !contentType.includes("application/json")) {
        throw new Error('Expected JSON response')
    }
    return await response.json() as T;
}

export function decodeXHRResponse<T>(xhr: XMLHttpRequest): T | undefined {
    if(xhr.responseText==='') {
        return undefined
    }
    var contentType = xhr.getResponseHeader("Content-Type") 
    if (!contentType || !contentType.includes("application/json")) {
        throw new Error('Expected JSON response')
    }
    return JSON.parse(xhr.responseText)
}

// Fetch any file and trigger a browser download.
// - Detects filename from Content-Disposition (RFC 5987) or URL if not provided.
// - Uses response.blob() (works for binary and text).
// - Preserves content-type; adds common extensions if missing.
export const fetchFile = async ({
  services,
  url,
  fileName,
  accept = "*/*",
  method = "GET",
  body,
  headers = {},
}: {
  services: Services
  url: string
  fileName?: string
  accept?: string
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
  body?: BodyInit | null
  headers?: Record<string, string>
}) => {
  try {
    const response = await services.session.fetchWithToken(url, {
      method,
      headers: {
        Accept: accept,
        ...headers,
      },
      body,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }

    const blob = await response.blob()
    const contentType =
      response.headers.get("content-type") ||
      headers["Content-Type"] ||
      "application/octet-stream"

    const nameFromHeader = getFileNameFromContentDisposition(
      response.headers.get("content-disposition"),
    )
    const nameFromUrl = deriveFileNameFromUrl(url)
    const finalName = ensureExtension(
      fileName || nameFromHeader || nameFromUrl || "download",
      contentType,
    )

    const typedBlob = blob.type ? blob : new Blob([blob], { type: contentType })

    const downloadUrl = window.URL.createObjectURL(typedBlob)
    const a = document.createElement("a")
    a.href = downloadUrl
    a.download = finalName
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error("Error fetching file:", error)
    throw error
  }
}

function getFileNameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return

  // filename* (RFC 5987)
  const starMatch = header.match(/filename\*\s*=\s*([^']*)''([^;]+)/i)
  if (starMatch) {
    try {
      // const enc = starMatch[1] // encoding, usually UTF-8
      const value = decodeURIComponent(starMatch[2])
      return value
    } catch {
      // fall through
    }
  }

  // filename="..."
  const match = header.match(/filename\s*=\s*"?([^";]+)"?/i)
  if (match) return match[1]
}

function deriveFileNameFromUrl(url: string): string | undefined {
  try {
    const u = new URL(url, window.location.origin)
    const last = u.pathname.split("/").filter(Boolean).pop()
    if (last) return last
  } catch {
    // ignore URL parsing errors
  }
  return
}

function ensureExtension(name: string, contentType?: string): string {
  if (!contentType) return name
  const extMap: Record<string, string> = {
    "text/csv": ".csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "application/pdf": ".pdf",
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "application/zip": ".zip",
  }
  const ext = extMap[contentType]
  if (!ext) return name
  if (name.toLowerCase().endsWith(ext)) return name
  if (/\.[a-z0-9]+$/i.test(name)) return name
  return `${name}${ext}`
}

export const fetchCSV = async ({
  services,
  url,
  fileName,
}: {
  services: Services
  url: string
  fileName: string
}) => {
  return fetchFile({ services, url, fileName, accept: "text/csv" })
}

export const fetchExcel = async ({
  services,
  url,
  fileName,
}: {
  services: Services
  url: string
  fileName: string
}) => {
  return fetchFile({
    services,
    url,
    fileName,
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
}

export const fetchPDF = async ({
  services,
  url,
  fileName,
}: {
  services: Services
  url: string
  fileName: string
}) => {
  return fetchFile({
    services,
    url,
    fileName,
    accept: "application/pdf",
  })
}

export const fetchHTML = async ({
  services,
  url,
  fileName,
}: {
  services: Services
  url: string
  fileName: string
}) => {
  return fetchFile({
    services,
    url,
    fileName,
    accept: "text/html",
  })
}